"use client";

import { useEffect, useRef } from "react";
import gsap from "gsap";
import { ScrollTrigger } from "gsap/ScrollTrigger";
import { useLocale, useTranslations } from "next-intl";
import { useGetMinds } from "@/hooks/main-hooks";
import { ICONS } from "@/data/main-data";
import Tilt from "@/component/common/Tilt";

gsap.registerPlugin(ScrollTrigger);

interface MindsetItem {
  _id: number | string;
  title: string;
  title_ar?: string;
  description: string;
  description_ar?: string;
  icon: string;
  bgText?: string;
}

/** Working principles as glass cards that tilt toward the pointer. */
export default function Mindset() {
  const t = useTranslations("brand.about.mindset");
  const locale = useLocale();
  const rtl = locale === "ar";
  const { data } = useGetMinds();
  const principles: MindsetItem[] = data?.data || [];
  const root = useRef<HTMLElement>(null);

  useEffect(() => {
    if (!root.current || !principles.length) return;
    const mm = gsap.matchMedia(root);
    mm.add("(prefers-reduced-motion: no-preference)", () => {
      gsap.from(".mi-line", {
        yPercent: 110,
        duration: 1.2,
        stagger: 0.08,
        ease: "expo.out",
        scrollTrigger: { trigger: ".mi-head", start: "top 80%", once: true },
      });
      gsap.from(".mi-sub", {
        opacity: 0,
        y: 20,
        duration: 1,
        ease: "expo.out",
        scrollTrigger: { trigger: ".mi-head", start: "top 75%", once: true },
      });
      gsap.from(".mi-item", {
        opacity: 0,
        y: 60,
        rotateX: 8,
        transformPerspective: 1000,
        duration: 1.1,
        stagger: 0.08,
        ease: "expo.out",
        clearProps: "transform",
        scrollTrigger: { trigger: ".mi-grid", start: "top 85%", once: true },
      });
    });
    return () => mm.revert();
  }, [principles.length]);

  if (!principles.length) return null;

  return (
    <section ref={root} className="relative px-5 py-28 md:px-10 md:py-40">
      <div className="mx-auto max-w-[1400px]">
        <div className="mi-head mb-16 grid gap-8 md:mb-24 md:grid-cols-[1fr_minmax(0,420px)] md:items-end">
          <div>
            <p className="label mb-6">{t("label")}</p>
            <h2 className="display text-[clamp(2.6rem,7vw,7rem)] text-bone">
              <span className="block overflow-hidden pb-[0.04em]">
                <span className="mi-line block">{t("title1")}</span>
              </span>
              <span className="block overflow-hidden pb-[0.08em]">
                <span className="mi-line block text-ash">{t("title2")}</span>
              </span>
            </h2>
          </div>
          <p className="mi-sub text-lg leading-relaxed text-ash">{t("sub")}</p>
        </div>

        <ol className="mi-grid grid gap-5 md:grid-cols-2 xl:grid-cols-3">
          {principles.map((item, i) => {
            const Icon = ICONS.find((ic) => ic.name === item.icon)?.component;
            const title = rtl && item.title_ar ? item.title_ar : item.title;
            const desc = rtl && item.description_ar ? item.description_ar : item.description;
            return (
              <li key={item._id} className="mi-item">
                <Tilt max={5} className="h-full rounded-[26px]">
                  <article className="group relative flex h-full flex-col gap-6 overflow-hidden rounded-[26px] border border-[var(--hair)] bg-void-2/55 p-7 backdrop-blur-xl transition-colors duration-500 hover:border-[var(--hair-strong)] md:p-8">
                    <span
                      aria-hidden="true"
                      className="absolute inset-x-0 top-0 h-px origin-left scale-x-0 bg-heat transition-transform duration-700 ease-[cubic-bezier(.16,1,.3,1)] group-hover:scale-x-100 rtl:origin-right"
                    />
                    <div className="flex items-center justify-between gap-4">
                      {Icon && (
                        <span className="grid h-11 w-11 place-items-center rounded-full border border-[var(--hair-strong)] text-ash transition-colors duration-500 group-hover:border-heat group-hover:text-heat">
                          <Icon size={17} strokeWidth={1.6} />
                        </span>
                      )}
                      <span className="tabular font-mono text-[12px] text-heat" dir="ltr">
                        {String(i + 1).padStart(2, "0")}
                      </span>
                    </div>
                    <h3 className="mt-6 max-w-[20ch] font-display text-[clamp(1.5rem,2.2vw,2rem)] font-bold leading-[1.08] tracking-[-0.03em] text-bone">
                      {title}
                    </h3>
                    <p className="leading-relaxed text-ash">{desc}</p>
                  </article>
                </Tilt>
              </li>
            );
          })}
        </ol>
      </div>
    </section>
  );
}
